import { createContext, useContext, type ReactNode } from "react";
import type { Catalog, Localized, SiteStatus } from "../../shared/types";
import { useAuth } from "../auth/AuthProvider";
import { api } from "./api";
import { DEFAULT_MP_FEE_PCT } from "../../shared/money";
import { CurrencyProvider } from "./CurrencyProvider";
import { GameProvider } from "./GameProvider";
import { useAsync } from "./useAsync";

interface CatalogValue {
  catalog: Catalog;
  settings: Record<string, Localized>;
  site: SiteStatus;
}

const EMPTY: CatalogValue = { catalog: { games: [], categories: [] }, settings: {}, site: { comingSoon: false } };
const CatalogContext = createContext<CatalogValue>(EMPTY);

/** Loads games, categories, site settings and site status once; the UI degrades gracefully if they fail. */
export function CatalogProvider({ children, value }: { children: ReactNode; value?: Partial<CatalogValue> & Pick<CatalogValue, "catalog" | "settings"> }) {
  // Beta testers and admins see the open store once signed in: re-read the site status on login/logout.
  const userId = useAuth().user?.id ?? null;
  const site = useAsync((signal) => (value ? Promise.resolve(null) : api.site(signal).catch(() => EMPTY.site)), [value, userId]);
  const { data } = useAsync(
    async (signal) => {
      if (value) return { ...EMPTY, ...value };
      const [catalog, settings] = await Promise.all([api.catalog(signal), api.settings(signal).catch(() => ({}) as Record<string, Localized>)]);
      return { catalog, settings, site: EMPTY.site };
    },
    [value],
  );
  const current = value ? { ...EMPTY, ...value } : { ...(data ?? EMPTY), site: site.data ?? EMPTY.site };
  return (
    <CatalogContext.Provider value={current}>
      <CurrencyProvider rate={current.site.usdArsRate ?? null} transferPrices={current.site.transferPrices ?? false} mpFeePct={current.site.mpFeePct ?? DEFAULT_MP_FEE_PCT}>
        <GameProvider games={current.catalog.games}>{children}</GameProvider>
      </CurrencyProvider>
    </CatalogContext.Provider>
  );
}

export function useCatalog(): CatalogValue {
  return useContext(CatalogContext);
}

/** True for signed-in admins / beta testers looking at the store before it opens. */
export function usePreview(): boolean {
  return Boolean(useContext(CatalogContext).site.preview);
}

/** True while the store runs in "Próximamente" mode (no accounts, cart or checkout). */
export function useComingSoon(): boolean {
  return useContext(CatalogContext).site.comingSoon;
}

/** False while the store sells nothing of its own (admin › Pedidos): the site is the market and its card catalog. */
export function useStoreSales(): boolean {
  return useContext(CatalogContext).site.storeSales !== false;
}
